import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Clock, Timer } from "lucide-react";
import { api } from "../../lib/api";
import { Badge } from "../ui/badge";

export function CaseStudyCard({ cs }) {
  return (
    <div className="rounded-2xl border border-white/10 bg-navy-800 overflow-hidden card-lift flex flex-col" data-testid={`case-card-${cs.case_id}`}>
      <div className="relative h-44 bg-navy-700">
        {cs.image_url && (
          <img src={cs.image_url} alt={cs.title} loading="lazy" className="w-full h-full object-cover opacity-70" />
        )}
        <div className="absolute inset-0 bg-gradient-to-t from-navy-800 to-transparent" />
        {cs.is_demo && (
          <Badge className="absolute top-4 right-4 bg-flame text-white border-transparent font-mono text-[9px] tracking-[0.2em]" data-testid={`case-demo-badge-${cs.case_id}`}>
            DEMO / CONCEPT
          </Badge>
        )}
        <Badge variant="outline" className="absolute top-4 left-4 border-white/20 text-slate-300 font-mono text-[9px] tracking-[0.2em] bg-navy-900/70">
          {cs.delivery_model}
        </Badge>
      </div>
      <div className="p-7 flex flex-col flex-1">
        <h3 className="font-heading text-xl font-bold text-white">{cs.title}</h3>
        <p className="mt-2 text-xs text-slate-500">{cs.client_label}</p>
        <p className="mt-4 text-sm text-slate-400 leading-relaxed line-clamp-3">{cs.problem}</p>
        <div className="mt-5 flex flex-wrap gap-4 font-mono text-[10px] tracking-wider text-slate-400">
          <span className="flex items-center gap-1.5"><Timer className="w-3.5 h-3.5 text-flame" /> {cs.estimated_hours}</span>
          <span className="flex items-center gap-1.5"><Clock className="w-3.5 h-3.5 text-flame" /> {cs.timeline}</span>
        </div>
        <div className="mt-5 flex flex-wrap gap-2">
          {(cs.features || []).slice(0, 4).map((f) => (
            <span key={f} className="rounded-full border border-white/10 px-3 py-1 text-[10px] text-slate-400">{f}</span>
          ))}
        </div>
      </div>
    </div>
  );
}

export default function CaseStudiesSection() {
  const [cases, setCases] = useState([]);

  useEffect(() => {
    api.get("/case-studies").then((res) => setCases(res.data.slice(0, 3))).catch(() => {});
  }, []);

  if (!cases.length) return null;

  return (
    <section className="py-24 lg:py-32 border-t border-white/5" data-testid="case-studies-section">
      <div className="container-x">
        <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-6">
          <div>
            <p className="section-label">CASE STUDIES</p>
            <h2 className="mt-5 font-heading text-4xl sm:text-5xl font-bold tracking-tight text-white">
              WHAT A BUILD <span className="text-flame">LOOKS LIKE.</span>
            </h2>
            <p className="mt-4 text-xs text-slate-500 max-w-xl">
              Current entries are demo/concept projects that illustrate scope, hours and timelines. Genuine client case
              studies are published only with client consent.
            </p>
          </div>
          <Link to="/case-studies" data-testid="cases-view-all-link" className="inline-flex items-center gap-2 font-heading font-bold text-sm text-flame hover:gap-3 transition-all shrink-0">
            ALL CASE STUDIES <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
        <div className="mt-14 grid grid-cols-1 md:grid-cols-3 gap-6">
          {cases.map((cs) => <CaseStudyCard key={cs.case_id} cs={cs} />)}
        </div>
      </div>
    </section>
  );
}
